import snakecaseKeys from 'snakecase-keys';

/**
 * Creates a TransformStream that converts Cognite AI API responses to OpenAI SSE format.
 *
 * Allows reusing OpenAI SDK clients with Cognite's streaming API by bridging format differences.
 */
export const createTransformationStream = () => {
  return new TransformStream({
    transform(chunk, controller) {
      // Cast required: TransformStream chunk type is unknown by default, but we know it's Uint8Array from fetch
      const content = new TextDecoder().decode(chunk as Uint8Array);

      if (content) {
        transformStream(content, controller);
      }
      if (!content) {
        controller.enqueue(undefined);
      }
    },
  });
};

/** Processes and enqueues transformed content into the stream controller. */
function transformStream(content: string, controller: TransformStreamDefaultController<Uint8Array>) {
  if (content) {
    const convertedLines = new TextEncoder().encode(convertLines(content));
    controller.enqueue(convertedLines);
  }

  if (!content) {
    controller.enqueue(undefined);
  }
}

/**
 * Converts newline-delimited JSON chunks to OpenAI SSE format.
 *
 * Injects required OpenAI fields (id, object, system_fingerprint, index) that Cognite's API doesn't provide,
 * and converts field naming from camelCase to snake_case for OpenAI compatibility.
 */
function convertLines(chunkContents: string) {
  const convertedLines = chunkContents
    .split('\n')
    .filter((line) => line)
    .map((line) => JSON.parse(line))
    .map((line) => snakecaseKeys(line))
    .map((lineObject) => `data: ${JSON.stringify(lineObject)}`);

  return [...convertedLines, ''].join('\n\n');
}
